// Catering summary for a date range: meals, dietary totals and names.
import React, { useEffect, useState } from 'react';
import { api } from '../api';
import { Spinner, ErrorBox, DateField } from '../ui';
import { todayDarwin, addDays, toAus, fmtDayLabel, csvDownload } from '../util';

export default function Catering() {
  const [from, setFrom] = useState(todayDarwin());
  const [to, setTo] = useState(addDays(todayDarwin(), 6));
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to || to < from) return;
    setData(null);
    api(`/catering?from=${from}&to=${to}`).then(setData).catch(e => setError(e.message));
  }, [from, to]);

  if (error) return <ErrorBox error={error} />;

  const exportCsv = () => {
    if (!data) return;
    const rows: any[][] = [];
    for (const d of data.days) {
      rows.push([toAus(d.date), d.total_onsite, d.arrivals, d.departures, '', '']);
      for (const [req, info] of Object.entries<any>(d.requirements)) {
        rows.push(['', '', '', '', `${req}${info.allergy ? ' (ALLERGY)' : ''} × ${info.count}`, info.names.join('; ')]);
      }
    }
    csvDownload(`catering-${from}-to-${to}.csv`, ['Date', 'People onsite (meals)', 'Arrival meals', 'Departure meals', 'Requirement', 'Names'], rows);
  };

  return (
    <div className="page">
      <div className="page-head">
        <h2>Catering summary</h2>
        <div className="btn-row">
          <span className="muted">From</span><div style={{ width: 130 }}><DateField value={from} onChange={v => v && setFrom(v)} /></div>
          <span className="muted">to</span><div style={{ width: 130 }}><DateField value={to} onChange={v => v && setTo(v)} /></div>
          <button className="btn" onClick={() => window.print()}>Print</button>
          <button className="btn" onClick={exportCsv}>Export CSV</button>
        </div>
      </div>
      <p className="muted">Counts include everyone onsite with meals required. <strong>⚠ = medically significant allergy</strong> — treat separately from preferences.</p>
      {!data ? <Spinner /> : data.days.map((d: any) => (
        <div key={d.date} className="panel">
          <h3>{fmtDayLabel(d.date)} — {toAus(d.date)}</h3>
          <div className="btn-row" style={{ marginBottom: 8 }}>
            <span className="chip" style={{ background: 'var(--olive)' }}>{d.total_onsite} people / meals</span>
            <span className="muted">{d.arrivals} arriving (arrival meals) · {d.departures} departing (departure meals)</span>
          </div>
          {Object.keys(d.requirements).length === 0
            ? <p className="muted">No special dietary requirements.</p>
            : Object.entries<any>(d.requirements).map(([req, info]) => (
              <div key={req} style={{ fontSize: 13, marginBottom: 4 }}>
                {info.allergy ? <span className="chip chip-allergy">⚠ {req} × {info.count}</span>
                  : <span className="chip" style={{ background: '#5f7a81' }}>{req} × {info.count}</span>}
                <span className="muted" style={{ marginLeft: 8 }}>{info.names.join(', ')}</span>
              </div>
            ))}
          {d.notes.length > 0 && <p className="muted">Notes: {d.notes.join(' · ')}</p>}
        </div>
      ))}
    </div>
  );
}
